// Scherm 2 — de pulsbalk. Vorm letterlijk uit de dummy (V1 r. 1676-1690):
// witte kaart, rand #ececec, radius 12, labels 13px #9ca3af met de waarde
// 14px/600 #374151 ernaast, sluitkruisje rechts.
//
// Wat er anders is dan de dummy (W8 scherm 2, oordeel "anders"):
//  · de vier getallen komen uit /api/meta in plaats van uit hardgecodeerde tekst
//  · "Top trending" is vandaag leeg en zegt WAAROM (de API stuurt null zolang de
//    berekening niet in de leeslaag zit — bak B in de veldenplaatsing)
//  · de delta-bakken komen erbij; zolang er één maandsnapshot is (baseline)
//    zeggen ze dat er nog niets te vergelijken valt in plaats van "0"

import type { ReactNode } from "react";
import { useState } from "react";

import { useMetaResource } from "../lib/meta";
import { useBaseline, useTrendsDelta } from "../lib/rounds";
import { color, radius, size, weight } from "../tokens";
import { EmptyReason } from "./Empty";

const DELTA_LABELS: Record<string, string> = {
  nieuw_op_de_radar: "nieuw op de radar",
  versnelt: "versnelt",
  nu_bevestigd: "nu bevestigd",
  nu_ook_in_arbeidsmarktdata: "nu ook in arbeidsmarktdata",
  stil_geworden: "stil geworden",
  uit_de_lijst: "uit de lijst",
};

function Item({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 7, alignItems: "baseline" }}>
      <span style={{ fontSize: size.meta, color: color.faint }}>{label}</span>
      <span style={{ fontSize: size.body, fontWeight: weight.medium, color: color.body }}>
        {children}
      </span>
    </div>
  );
}

export function PulseBar() {
  const [visible, setVisible] = useState(true);
  const baseline = useBaseline();
  // useMetaResource() en niet api.meta(): de metadata is één eigenschap van de
  // dataset en wordt module-breed gedeeld. Een eigen useResource hier haalde
  // hem een tweede keer op, bij élk scherm dat de pulsbalk toont.
  const meta = useMetaResource();
  // Dezelfde gedeelde delta als useBaseline gebruikt; een eigen useResource hier
  // stelde de database dezelfde vraag een tweede keer (gemeten 2026-08-24).
  const d = useTrendsDelta();

  if (!visible) return null;

  const m = meta.data;
  const buckets = d?.buckets ?? {};
  const filled = Object.entries(buckets)
    .filter(([, rows]) => Array.isArray(rows) && rows.length > 0)
    .slice(0, 3);

  return (
    <div
      style={{
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        padding: "14px 20px",
        marginBottom: 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
      }}
    >
      <div style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
        {meta.error && <EmptyReason reason={`Pulsbalk: ${meta.error}`} />}
        {!meta.error && (
          <>
            {/* In ronde 1 is élke skill "nieuw"; dan is dit geen nieuws maar de
                omvang van de eerste meting, en zegt het label dat ook. */}
            <Item label={baseline ? "Skills" : "Nieuwe skills"}>
              {meta.loading ? "…" : (m?.newSkillsThisRun ?? 0).toLocaleString("nl-NL")}
            </Item>
            <Item label="Meest in beweging">
              {meta.loading ? (
                "…"
              ) : m?.topTrendingSkill ? (
                m.topTrendingSkill.name
              ) : (
                <EmptyReason reason="wacht op de berekening in de leeslaag" />
              )}
            </Item>
            <Item label="Gepubliceerde bronnen">
              {meta.loading ? "…" : (m?.approvedSourceCount ?? 0).toLocaleString("nl-NL")}
            </Item>
          </>
        )}
        {d && (
          <>
            {baseline || filled.length === 0 ? null : (
              filled.map(([name, rows]) => (
                <Item key={name} label={DELTA_LABELS[name] ?? name}>
                  {(rows as unknown[]).length}
                </Item>
              ))
            )}
          </>
        )}
      </div>
      <button
        onClick={() => setVisible(false)}
        aria-label="Pulsbalk sluiten"
        style={{
          background: "none",
          border: "none",
          color: color.disabled,
          cursor: "pointer",
          padding: 4,
          borderRadius: 6,
          lineHeight: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M2 2l10 10M12 2L2 12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}
